import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { History, Mail, MessageSquare, RefreshCw, X, ShieldAlert } from "lucide-react";
import { fmtRelative } from "@/lib/format";

const BAND_TONE = {
  accelerating: "text-emerald-700 bg-emerald-50 border-emerald-300",
  steady: "text-sky-700 bg-sky-50 border-sky-300",
  cooling: "text-amber-700 bg-amber-50 border-amber-300",
  stalled: "text-rose-700 bg-rose-50 border-rose-300",
};

export default function PulseAlertEventsDialog({ wsId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brandOpts, setBrandOpts] = useState([]);
  const [band, setBand] = useState("_all");
  const [brandId, setBrandId] = useState("_all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [delivered, setDelivered] = useState("_all");

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    const p = new URLSearchParams();
    if (band !== "_all") p.set("band", band);
    if (brandId !== "_all") p.set("brand_id", brandId);
    if (fromDate) p.set("from_date", fromDate);
    if (toDate) p.set("to_date", toDate + "T23:59:59Z");
    if (delivered !== "_all") p.set("delivered", delivered);
    p.set("limit", "500");
    try {
      const r = await api.get(`/workspaces/${wsId}/brands/pulse/alerts/events?${p.toString()}`);
      setData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load events")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [wsId, band, brandId, delivered]);

  useEffect(() => {
    // Pull brand list once for the filter dropdown
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/brands/pulse/portfolio`)
      .then((r) => setBrandOpts((r.data.rows || []).map((x) => ({ id: x.brand_id, name: x.brand_name }))))
      .catch(() => {});
  }, [wsId]);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const uniqueBrands = data?.unique_brands || 0;
  const deliveredEmail = data?.delivered_email || 0;
  const deliveredSlack = data?.delivered_slack || 0;
  const byBand = data?.by_band || {};

  const resetFilters = () => {
    setBand("_all"); setBrandId("_all"); setFromDate(""); setToDate(""); setDelivered("_all");
    setTimeout(() => load(), 0);
  };

  const hasFilters = band !== "_all" || brandId !== "_all" || fromDate || toDate || delivered !== "_all";

  const exportCsv = () => {
    if (!rows.length) { toast.error("Nothing to export"); return; }
    const header = ["triggered_at", "brand_name", "band", "current", "delta", "reasons",
                    "delivered_email_count", "delivered_slack", "triggered_by"];
    const csv = [header.join(",")].concat(
      rows.map((r) => header.map((k) => {
        const v = r[k];
        if (Array.isArray(v)) return `"${v.join("; ").replaceAll('"', '""')}"`;
        if (v === null || v === undefined) return "";
        return typeof v === "string" ? `"${v.replaceAll('"', '""')}"` : v;
      }).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pulse-alert-events-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row(s)`);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-6xl" data-testid="pulse-events-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <History className="w-4 h-4 text-primary" />
            Pulse alert events
            <Badge variant="outline" className="rounded-none text-[10px]">{total} row(s)</Badge>
            <div className="ml-auto flex items-center gap-1">
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={load}
                        data-testid="pulse-events-refresh-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={exportCsv}
                        data-testid="pulse-events-export-btn">
                Export CSV
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="pulse-events-kpis">
          <KpiCard label="Events" value={total} />
          <KpiCard label="Brands" value={uniqueBrands} />
          <KpiCard label="Emailed" value={deliveredEmail} tone="text-sky-700" />
          <KpiCard label="Slack posts" value={deliveredSlack} tone="text-violet-700" />
          <KpiCard label="Stalled" value={byBand.stalled || 0} tone="text-rose-700" />
        </div>

        {/* Filters */}
        <div className="border border-border bg-card p-2 flex items-center gap-2 flex-wrap" data-testid="pulse-events-filters">
          <Select value={band} onValueChange={setBand}>
            <SelectTrigger className="h-8 w-36 rounded-none text-xs" data-testid="pulse-events-band-filter"><SelectValue placeholder="Band" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All bands</SelectItem>
              <SelectItem value="accelerating">Accelerating</SelectItem>
              <SelectItem value="steady">Steady</SelectItem>
              <SelectItem value="cooling">Cooling</SelectItem>
              <SelectItem value="stalled">Stalled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={brandId} onValueChange={setBrandId}>
            <SelectTrigger className="h-8 w-48 rounded-none text-xs" data-testid="pulse-events-brand-filter"><SelectValue placeholder="Brand" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="_all">All brands</SelectItem>
              {brandOpts.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={delivered} onValueChange={setDelivered}>
            <SelectTrigger className="h-8 w-36 rounded-none text-xs" data-testid="pulse-events-delivered-filter"><SelectValue placeholder="Delivered" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Any delivery</SelectItem>
              <SelectItem value="email">Email sent</SelectItem>
              <SelectItem value="slack">Slack posted</SelectItem>
              <SelectItem value="any">Any channel</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                   className="h-8 w-36 rounded-none text-xs" placeholder="From"
                   data-testid="pulse-events-from-date" />
          <span className="text-[10px] text-muted-foreground">→</span>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                   className="h-8 w-36 rounded-none text-xs" placeholder="To"
                   data-testid="pulse-events-to-date" />
          <Button variant="outline" size="sm" className="rounded-none h-8 text-xs" onClick={load}
                    data-testid="pulse-events-apply-btn">Apply</Button>
          {hasFilters && (
            <Button variant="ghost" size="sm" className="rounded-none h-8 text-xs" onClick={resetFilters}
                      data-testid="pulse-events-reset-btn">
              <X className="w-3 h-3 mr-0.5" /> Reset
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="border border-border bg-card max-h-[440px] overflow-auto" data-testid="pulse-events-table">
          {loading ? (
            <div className="text-sm text-muted-foreground p-6 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground p-6 text-center italic">
              No alert events match your filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/30 sticky top-0 z-10">
                <tr>
                  <TH>When</TH>
                  <TH>Brand</TH>
                  <TH align="right">Pulse</TH>
                  <TH>Band</TH>
                  <TH align="right">Δ</TH>
                  <TH>Reasons</TH>
                  <TH align="center">Email</TH>
                  <TH align="center">Slack</TH>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-border hover:bg-muted/20"
                        data-testid={`pulse-events-row-${r.brand_id}-${i}`}>
                    <td className="px-2 py-1.5 text-xs whitespace-nowrap">{fmtRelative(r.triggered_at)}</td>
                    <td className="px-2 py-1.5 text-xs font-medium truncate max-w-[200px]">
                      <span className="inline-flex items-center gap-1">
                        <ShieldAlert className="w-3 h-3 text-rose-500" />
                        {r.brand_name}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-xs font-mono text-right">{r.current ?? "—"}</td>
                    <td className="px-2 py-1.5 text-xs">
                      <span className={`text-[10px] uppercase border px-1 py-0.5 ${BAND_TONE[r.band] || ""}`}>{r.band}</span>
                    </td>
                    <td className="px-2 py-1.5 text-xs font-mono text-right">
                      {r.delta === null || r.delta === undefined ? "—" : (r.delta > 0 ? "+" : "") + r.delta}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-muted-foreground max-w-[280px]">
                      {(r.reasons || []).join(" · ")}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {(r.delivered_email_count || 0) > 0 ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-sky-700">
                          <Mail className="w-3 h-3" /> {r.delivered_email_count}
                        </span>
                      ) : <span className="text-[10px] text-muted-foreground">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {r.delivered_slack ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-violet-700">
                          <MessageSquare className="w-3 h-3" /> ✓
                        </span>
                      ) : <span className="text-[10px] text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TH({ children, align }) {
  return (
    <th className={`px-2 py-2 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border font-semibold whitespace-nowrap text-${align || "left"}`}>
      {children}
    </th>
  );
}

function KpiCard({ label, value, tone = "" }) {
  return (
    <div className="border border-border p-2 bg-card">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`text-xl font-mono font-black mt-1 ${tone}`}>{value}</div>
    </div>
  );
}
